<template>
  <div v-on-click-outside="closePanel" :class="hasBorders ? $style.inputContainer : $style.tagsContainer">
    <div :class="$style.displayedTags" @click="hasBorders && toggleTagsPanel()">
      <div :style="{ display: 'flex' }">
        <Chip v-for="(tag, index) in visibleTags" :key="index" :label="tag.name" class="p-mr-2" :class="$style.chip" />
        <Chip v-if="hasMore" :label="`+${remainingCount}`" class="p-mr-2" :class="$style.chip" />
      </div>
      <Button
        icon="pi pi-chevron-down"
        class="p-button-rounded p-button-text"
        :disabled="disabled"
        :class="$style.dropdownButton"
        @click.stop="toggleTagsPanel"
      />
    </div>
    <VTMultiSelect
      v-if="showPanel"
      :items="formattedTags"
      :selected-items="selectedTags"
      :class="$style.tagsSelector"
      @add-item="handleAddTag"
      @delete-item="handleDeleteTag"
      @toggle-selection="handleToggleTagSelection"
    />
  </div>
</template>

<script setup>
import VTMultiSelect from "@/components/VTMultiSelect";
import { useItems } from "@/modules/Items/hooks/useItems";
import useTags from "@/modules/Items/hooks/useTags";
import { vOnClickOutside } from "@vueuse/components";
import Button from "primevue/button";
import Chip from "primevue/chip";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import useToast from "@/hooks/useToastNotification";

const props = defineProps({
  modelValue: Array,
  gameId: String,
  itemId: String,
  hasBorders: Boolean,
  type: String,
  versionId: String,
  maxVisibleTags: {
    type: Number,
    default: 4,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);
const showPanel = ref(false);

const confirm = useConfirm();

const { refetchItems } = useItems(props.gameId, props.versionId);
const { toastError } = useToast();

const {
  tags: gameTags,
  createTag,
  deleteTag: deleteTagMutation,
} = useTags(props.gameId, props.type && { type: props.type });

const formattedTags = computed(() => gameTags.value?.map((tag) => ({ name: tag.name, id: tag.id, isDeletable: true })));

const selectedTags = ref(props.modelValue);

const visibleTags = computed(() => selectedTags.value.slice(0, props.maxVisibleTags));
const remainingCount = computed(() => selectedTags.value.length - props.maxVisibleTags);
const hasMore = computed(() => selectedTags.value.length > props.maxVisibleTags);

function toggleTagsPanel() {
  showPanel.value = !showPanel.value;
}

function closePanel() {
  showPanel.value = false;
}

async function handleAddTag(tagName) {
  const trimmedTagName = tagName.trim();

  if (!trimmedTagName || selectedTags.value.some((t) => t.name === trimmedTagName)) {
    return;
  }

  if (["OFFER", "SHOP_ITEM"].includes(props.type) && trimmedTagName.includes(";")) {
    toastError({
      detail: "Tag name cannot contain semicolons (;)",
      summary: "Error",
      life: 3000,
    });
    return;
  }

  const newTag = { name: trimmedTagName };

  try {
    const createdTag = await createTag(trimmedTagName);

    newTag.id = createdTag.id;
  } catch (error) {
    console.error("Failed to create tag:", error);
    return;
  }

  selectedTags.value = [...selectedTags.value, newTag];
  emitUpdate();
}

function handleToggleTagSelection(tag) {
  const index = selectedTags.value.findIndex((selectedTag) => selectedTag.name === tag.name);
  if (index > -1) {
    const newTags = [...selectedTags.value];
    newTags.splice(index, 1);
    selectedTags.value = newTags;
  } else {
    selectedTags.value = [...selectedTags.value, tag];
  }
  emitUpdate();
}

function handleDeleteTag(tag) {
  confirm.require({
    message: `Do you want to delete the tag ${tag.name}?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: () => {
      deleteTag(tag);
    },
  });
}

async function deleteTag(tag) {
  await deleteTagMutation(tag.id);
  await refetchItems();
}

function emitUpdate() {
  emit("update:modelValue", selectedTags.value);
}
</script>

<style module lang="scss">
.chip {
  margin-left: 5px;
}

.tagsContainer {
  display: flex;
  flex-direction: column;
  position: relative;
  font-weight: 400;
  width: 100%;

  .displayedTags {
    display: flex;
    align-items: center;
    gap: 5px;
    justify-content: space-between;
  }

  .tagsSelector {
    position: absolute;
    width: 100%;
    top: 100%;
    left: 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #d1d5db;
    min-width: 260px;
  }
}

.inputContainer {
  display: flex;
  position: relative;
  font-weight: 400;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 100%;

  .displayedTags {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    cursor: pointer;
  }

  .chip {
    font-size: 12px;
  }

  .tagsSelector {
    position: fixed;
    width: 458px;
    top: 314px;
    left: 21px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #d1d5db;
    border-radius: 6px;
  }
}
</style>
